import Vue from 'vue'

import { createProvider } from '@/vue-apollo'
import gql from 'graphql-tag'
import _ from 'lodash'
import omitDeep from 'omit-deep-lodash'

import fetchProjectListQuery from '@/graphql/queries/fetchProjectList.gql'
import fetchProjectProposalQuery from '@/graphql/GetProjectProposal.gql'
import patchProjectProposalMutation from '@/graphql/mutations/patchProjectProposal.gql'
import advanceProjectProposalMutation from '@/graphql/mutations/advanceProjectProposal.gql'
import advanceProjectProposalWithApprovingAdvanceFundMutation from '@/graphql/mutations/advanceProjectProposalWithApprovingAdvanceFund.gql'
import advanceProjectClosureApprovalWithCommentFromAdvisorMutation from '@/graphql/mutations/advanceProjectClosureWithCommentFromAdvisor.gql'
import rejectProjectProposalMutation from '@/graphql/mutations/rejectProjectProposal.gql'
import loadProjectChangeHistory from '@/graphql/queries/loadProjectChangeHistory.gql'
import loadProjectChangeRequest from '@/graphql/queries/loadProjectChangeRequest.gql'
import loadProjectBasicInfo from '@/graphql/queries/loadProjectBasicInfo.gql'
import loadProjectPlanning from '@/graphql/queries/loadProjectPlanning.gql'
import loadProjectBudget from '@/graphql/queries/loadProjectBudget.gql'
import loadProjectOutcome from '@/graphql/queries/loadProjectOutcome.gql'
import loadProjectOther from '@/graphql/queries/loadProjectOther.gql'
import loadProjectDocuments from '@/graphql/queries/loadProjectDocuments.gql'
import setProjectLeader from '@/graphql/mutations/setProjectLeader.gql'

const apolloClient = createProvider().defaultClient
const loadingStateKey = 'project'

const state = {
  all: [],
  filters: {
    search: '',
    status: null,
    group: null,
    statusFilterOptions: [
      { value: null, text: 'ทุกสถานะ' },
      { value: { field: 'status', keyword: 0 }, text: 'แบบร่าง' },
      { value: { field: 'status', keyword: 13 }, text: 'อยู่ระหว่างการพิจารณาจากประธานโครงการ' },
      { value: { field: 'status', keyword: 6 }, text: 'อยู่ระหว่างการพิจารณาจากประธานหน่วยงาน' },
      { value: { field: 'status', keyword: 1 }, text: 'อยู่ระหว่างการพิจารณาจากที่ปรึกษา' },
      { value: { field: 'status', keyword: 12 }, text: 'อยู่ระหว่างการพิจารณาของสโมสรนักศึกษา' },
      { value: { field: 'status', keyword: 10 }, text: 'อยู่ระหว่างการพิจารณาของเจ้าหน้าที่กิจการนักศึกษาคณะ' },
      { value: { field: 'status', keyword: 11 }, text: 'อยู่ระหว่างการพิจารณาของผู้ช่วย/รองคณบดี' },
      { value: { field: 'status', keyword: 9 }, text: 'อยู่ระหว่างการพิจารณาจากสภานักศึกษา (งบสำรองจ่าย)' },
      { value: { field: 'status', keyword: 2 }, text: 'กิจการนักศึกษาพิจารณา' },
      { value: { field: 'status', keyword: 3 }, text: 'อนุมัติจัดโครงการ' },

      { value: { field: 'status', keyword: 15 }, text: 'อยู่ระหว่างการพิจารณาปิดจากประธานโครงการ' },
      { value: { field: 'status', keyword: 16 }, text: 'อยู่ระหว่างการพิจารณาปิดจากประธานหน่วยงาน' },
      { value: { field: 'status', keyword: 17 }, text: 'อยู่ระหว่างการพิจารณาปิดจากที่ปรึกษา' },
      { value: { field: 'status', keyword: 18 }, text: 'อยู่ระหว่างการพิจารณาปิดของสโมสรนักศึกษา' },
      { value: { field: 'status', keyword: 19 }, text: 'อยู่ระหว่างการพิจารณาปิดของเจ้าหน้าที่กิจการนักศึกษาคณะ' },
      { value: { field: 'status', keyword: 20 }, text: 'อยู่ระหว่างการพิจารณาปิดของผู้ช่วย/รองคณบดี' },

      { value: { field: 'status', keyword: 7 }, text: 'กิจการนักศึกษาพิจารณาปิด' },
      { value: { field: 'status', keyword: 8 }, text: 'ปิดโครงการ' },
      { value: { field: 'status', keyword: 4 }, text: 'ที่ปรึกษาไม่อนุมัติ' },
      { value: { field: 'status', keyword: 5 }, text: 'กิจการนักศึกษาไม่อนุมัติ' }
    ],
    sortBy: { field: 'createdAt', type: 'DESC' },
    sortByOptions: [
      { value: { field: 'createdAt', type: 'DESC' }, text: 'วันที่สร้างโครงการ (ใหม่ขึ้นก่อน)' },
      { value: { field: 'createdAt', type: 'ASC' }, text: 'วันที่สร้างโครงการ (เก่าขึ้นก่อน)' },
      { value: { field: 'status', type: 'ASC' }, text: 'สถานะ' },
      { value: { field: 'groupId', type: 'ASC' }, text: 'หน่วยงาน' }
    ],
    showReleventOnly: false,

    perPage: 100,
    page: 1
  },
  paginationInfo: {
    total: 0,
    count: 0,
    currentPage: 1,
    hasMorePages: false,
    lastPage: 1
  }
}

const actions = {
  fetchAllProjects: async ({ commit, state }, noLoadingState = false) => {
    if (!noLoadingState) { commit('setLoadingState', { key: loadingStateKey, isLoading: true }, { root: true }) }

    let filters = []

    if (state.filters.search !== '') {
      filters.push({ field: 'name', keyword: state.filters.search })
    }

    if (state.filters.group !== null) {
      filters.push(state.filters.group)
    }
    if (state.filters.status !== null) {
      filters.push(state.filters.status)
    }

    const result = await apolloClient.query({
      query: fetchProjectListQuery,
      variables: {
        count: state.filters.perPage,
        page: state.filters.page,
        sort: state.filters.sortBy,
        filters
      },
      fetchPolicy: 'no-cache'
    })
    commit('setProjectPaginationInfo', result.data.projects.paginatorInfo)
    commit('setAllProjects', result.data.projects.data.filter(project => state.filters.showReleventOnly ? project.isReleventToOsdaUser === true : true))
    commit('setLoadingState', { key: loadingStateKey, isLoading: false }, { root: true })
  },
  fetchProject: async ({ commit }, id) => {
    commit('setLoadingState', { key: loadingStateKey, isLoading: true }, { root: true })
    const result = await apolloClient.query({ query: fetchProjectProposalQuery, variables: { id }, fetchPolicy: 'no-cache' })
    commit('setProject', result.data.project)
    commit('setLoadingState', { key: loadingStateKey, isLoading: false }, { root: true })
  },
  patchProposal: async ({ commit }, proposal) => {
    try {
      const result = await apolloClient.mutate({
        mutation: patchProjectProposalMutation,
        variables: {
          input: {
            id: proposal.id,
            patch: _.omit(
              omitDeep(proposal, ['__typename', 'approvedAdvance']),
              [
                'id', 'status', 'group', 'advisor', 'signedDoc',
                'author', 'statusText', 'acYear', 'isCheckIssuingPossible',
                'checkingStatus', 'checking', 'osdaRejectReason', 'changeHistory',
                'route', 'action'
              ]
            )
          }
        }
      })
      commit('setProject', result.data.projectEdit)
    } catch (error) {
      throw error
    }
  },
  deleteProject: ({ commit, dispatch }, id) => {
    return apolloClient.mutate({
      mutation: gql`mutation ($id: ID!) { projectDelete(id: $id) { id } }`,
      variables: { id }
    })
  },
  advance: async ({ commit, dispatch, getters }, id) => {
    const result = await apolloClient.mutate({
      mutation: advanceProjectProposalMutation,
      variables: { id }
    })

    commit('setProject', result.data.projectAdvance)
  },
  approveAdvanceFund: async ({ commit, dispatch, getters }, { id, amount }) => {
    const result = await apolloClient.mutate({
      mutation: advanceProjectProposalWithApprovingAdvanceFundMutation,
      variables: { id, amount }
    })

    commit('setProject', result.data.projectAdvanceWithApprovingAdvanceFund)
  },
  advanceWithAdvisorComment: async ({ commit, dispatch, getters }, { id, comment }) => {
    const result = await apolloClient.mutate({
      mutation: advanceProjectClosureApprovalWithCommentFromAdvisorMutation,
      variables: { id, comment }
    })

    commit('setProject', result.data.projectAdvanceWithCommentFromAdvisor)
  },
  reject: async ({ commit }, { id, reason }) => {
    const result = await apolloClient.mutate({
      mutation: rejectProjectProposalMutation,
      variables: { id, reason }
    })

    commit('setProject', result.data.projectReject)
  },
  loadChangeHistory: async ({ commit, state }, id) => {
    const result = await apolloClient.query({ query: loadProjectChangeHistory, variables: { id }, fetchPolicy: 'no-cache' })
    commit('setProjectField', { id: id, key: 'changeHistory', data: result.data.project.changeHistory })
  },
  loadBasicInfo: async ({ commit }, id) => {
    const result = await apolloClient.query({ query: loadProjectBasicInfo, variables: { id }, fetchPolicy: 'no-cache' })
    Object.keys(result.data.project).map(key => {
      commit('setProjectField', { id: id, data: result.data.project[key], key })
    })
  },
  loadPlanning: async ({ commit }, id) => {
    const result = await apolloClient.query({ query: loadProjectPlanning, variables: { id }, fetchPolicy: 'no-cache' })
    commit('setProjectField', { id: id, key: 'plannedTasks', data: result.data.project.plannedTasks })
  },
  loadBudget: async ({ commit }, id) => {
    const result = await apolloClient.query({ query: loadProjectBudget, variables: { id }, fetchPolicy: 'no-cache' })
    Object.keys(result.data.project).map(key => {
      commit('setProjectField', { id: id, data: result.data.project[key], key })
    })
  },
  loadOutcome: async ({ commit }, id) => {
    const result = await apolloClient.query({ query: loadProjectOutcome, variables: { id }, fetchPolicy: 'no-cache' })
    Object.keys(result.data.project).map(key => {
      commit('setProjectField', { id: id, key: 'expectedOutcomes', data: result.data.project.expectedOutcomes })
    })
  },
  loadOther: async ({ commit }, id) => {
    const result = await apolloClient.query({ query: loadProjectOther, variables: { id }, fetchPolicy: 'no-cache' })
    Object.keys(result.data.project).map(key => {
      commit('setProjectField', { id: id, data: result.data.project[key], key })
    })
  },
  loadDocuments: async ({ commit }, id) => {
    const result = await apolloClient.query({ query: loadProjectDocuments, variables: { id }, fetchPolicy: 'no-cache' })
    Object.keys(result.data.project).map(key => {
      commit('setProjectField', { id: id, data: result.data.project[key], key })
    })
  },
  loadChangeRequests: async ({ commit, state }, id) => {
    const result = await apolloClient.query({ query: loadProjectChangeRequest, variables: { id }, fetchPolicy: 'no-cache' })
    commit('setProjectField', { id: id, key: 'changeRequests', data: result.data.project.changeRequests })
    commit('setProjectField', { id: id, key: 'pendingChangeRequests', data: result.data.project.pendingChangeRequests })
  },
  setHeadOfProject: async ({ commit }, { id, payload }) => {
    try {
      const result = await apolloClient.mutate({
        mutation: setProjectLeader,
        variables: {
          id: id,
          input: payload
        }
      })

      commit('setProjectField', { id: id, key: 'leader', data: result.data.projectSetHeadOfProject })
    } catch (error) {
      throw new Error(error)
    }
  },
  createEvent: async ({ commit }, id) => {
    try {
      const result = await apolloClient.mutate({
        mutation: gql`mutation ($id: ID!) { projectCreateEvent(id: $id) { event { id }, eventUrl } }`,
        variables: { id }
      })

      commit('setProjectField', { id: id, key: 'event', data: result.data.projectCreateEvent })
    } catch (error) {
      throw new Error(error)
    }
  }
}

const mutations = {
  setAllProjects: (state, projects) => { state.all = projects },
  setProjectPaginationInfo: (state, paginationInfo) => { state.paginationInfo = paginationInfo },
  setProject: (state, projectProposal) => {
    let index = state.all.findIndex(project => project.id === projectProposal.id)
    if (index === -1) { index = state.all.length }
    Vue.set(state.all, index, projectProposal)
  },
  setGroupFilter: (state, group) => { state.filters.group = group },
  setSearchKeyword: (state, keyword) => { state.filters.search = keyword },
  setStatusFilter: (state, status) => { state.filters.status = status },
  setShowReleventOnlyFilter: (state, status) => { state.filters.showReleventOnly = status },
  setPerPageFilter: (state, perPage) => { state.filters.perPage = perPage },
  setSortByFilter: (state, sortBy) => { state.filters.sortBy = sortBy },
  setPageFilter: (state, page) => { state.filters.page = page },
  setProjectField: (state, { id, key, data }) => {
    let index = state.all.findIndex(project => project.id === id)
    if (index === -1) { throw new Error('Cannot set extra field before the project was loaded') }
    Vue.set(state.all[index], key, data)
    // state.all[index][key] = data
  }
}

const getters = {
  getFilteredProjectList (state) {
    return state.all.filter(project => {
      return project.name.indexOf(state.filters.search) > -1 &&
        (state.filters.status !== null ? project.status === state.filters.status : true)
    })
  },
  getProject (state) {
    return id => state.all.find(project => project.id === id)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
